import { type CSSProperties, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Tooltips for every icon-only button and link, and every symbol (bugs.md): what it is, in words. One
 * host for the whole app, mounted once. It finds its words on the nearest control or symbol: `data-tip`,
 * else its `title` (taken over so the browser's own doesn't show too), else its `aria-label` when it
 * shows no text of its own, or always for a symbol (`role="img"`, whose text is an emoji).
 * Hover after a beat, keyboard focus at once, and a long press on a phone (whose tap is then dropped).
 */
const SEL = 'button, a, [role="button"], [data-tip], [title], [role="img"][aria-label]'
const HOVER_MS = 450
const PRESS_MS = 500

function tipFor(el: Element | null): { el: HTMLElement; text: string } | null {
  const t = el?.closest<HTMLElement>(SEL)
  if (!t || t.getAttribute('role') === 'switch' || t.hasAttribute('disabled')) return null
  if (t.title) {
    t.dataset.tip = t.title
    t.removeAttribute('title')
  }
  const symbol = t.getAttribute('role') === 'img'
  const text = t.dataset.tip || (symbol || !t.textContent?.trim() ? t.getAttribute('aria-label') : null)
  return text ? { el: t, text } : null
}

export function TooltipHost() {
  const [tip, setTip] = useState<{ text: string; r: DOMRect } | null>(null)
  useEffect(() => {
    let timer = 0
    let pressed = false
    const hide = () => {
      clearTimeout(timer)
      setTip(null)
    }
    const show = (t: { el: HTMLElement; text: string }) =>
      setTip({ text: t.text, r: t.el.getBoundingClientRect() })
    const over = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return
      const t = tipFor(e.target as Element)
      clearTimeout(timer)
      if (!t) return setTip(null)
      timer = window.setTimeout(() => show(t), HOVER_MS)
    }
    const down = (e: PointerEvent) => {
      hide()
      pressed = false
      if (e.pointerType === 'mouse') return
      const t = tipFor(e.target as Element)
      if (t)
        timer = window.setTimeout(() => {
          pressed = true
          show(t)
        }, PRESS_MS)
    }
    const up = () => {
      clearTimeout(timer)
      if (pressed) timer = window.setTimeout(() => setTip(null), 1500)
    }
    // A long press shows the words; it shouldn't also press the button.
    const click = (e: MouseEvent) => {
      if (!pressed) return hide()
      pressed = false
      e.preventDefault()
      e.stopPropagation()
    }
    const focus = (e: FocusEvent) => {
      const el = e.target as HTMLElement
      const t = el.matches?.(':focus-visible') ? tipFor(el) : null
      if (t) show(t)
    }
    const opts = { capture: true, passive: true }
    document.addEventListener('pointerover', over, opts)
    document.addEventListener('pointerdown', down, opts)
    document.addEventListener('pointerup', up, opts)
    document.addEventListener('pointercancel', hide, opts)
    document.addEventListener('click', click, true)
    document.addEventListener('focusin', focus, opts)
    document.addEventListener('focusout', hide, opts)
    window.addEventListener('scroll', hide, opts)
    window.addEventListener('keydown', hide, opts)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('pointerover', over, opts)
      document.removeEventListener('pointerdown', down, opts)
      document.removeEventListener('pointerup', up, opts)
      document.removeEventListener('pointercancel', hide, opts)
      document.removeEventListener('click', click, true)
      document.removeEventListener('focusin', focus, opts)
      document.removeEventListener('focusout', hide, opts)
      window.removeEventListener('scroll', hide, opts)
      window.removeEventListener('keydown', hide, opts)
    }
  }, [])
  if (!tip) return null
  const at = tipPlace(tip.r)
  return createPortal(
    <div role="tooltip" className={at.className} style={at.style}>
      {tip.text}
    </div>,
    document.body,
  )
}

/**
 * Where a tooltip goes for a control at `r`: above it, or below it near the top of the screen. Centered
 * on it, except near an edge, where it lines up with that edge instead of running off the screen. The
 * console's richer cell tips (apps/mod cellTip.tsx) sit the same way.
 */
export function tipPlace(r: DOMRect): { className: string; style: CSSProperties } {
  const below = r.top < 48
  const mid = r.left + r.width / 2
  const w = window.innerWidth
  const side =
    mid < 130
      ? { left: Math.max(8, r.left) }
      : mid > w - 130
        ? { right: Math.max(8, w - r.right) }
        : { left: mid }
  return {
    className: `fm-tooltip${below ? ' fm-tooltip--below' : ''}${'right' in side ? ' fm-tooltip--end' : side.left === mid ? ' fm-tooltip--mid' : ''}`,
    style: { ...side, top: below ? r.bottom + 8 : r.top - 8 },
  }
}
